/* eslint-disable import/prefer-default-export  */
import React from 'react';
import Hammer from 'hammerjs';
import { useHistory, useLocation } from 'react-router';
import queryString from 'querystring';
import { shallowEqual } from 'react-redux';
import { queryParams } from './utils/fetch';
import * as styles from './hooks.scss';
import moment, { Moment } from 'moment';

// https://epicreact.dev/the-latest-ref-pattern-in-react/
export function useLatestRef<T>(value: T): React.MutableRefObject<T> {
  const ref = React.useRef(value);
  React.useLayoutEffect(() => {
    ref.current = value;
  }, [value]);
  return ref;
}

export function useEventListener<
  T extends HTMLElement | Window | Document,
  P extends Parameters<T['addEventListener']>
>(target: T | null, ...params: P) {
  const [name, handler, options] = params;

  if (typeof handler !== 'function') {
    throw new Error('handler must be a function. This hook does not support EventListenerObject');
  }

  const handlerRef = useLatestRef(handler);

  React.useEffect(() => {
    // I can't call hooks conditionally, so this needs to make the effect a NOOP
    if (target == null) {
      return () => {};
    }

    try {
      const stableHandler: typeof handlerRef.current = (...args) => handlerRef.current(...args);

      target.addEventListener(name, stableHandler, options);
      return function cleanup() {
        target.removeEventListener(name, stableHandler);
      };
    } catch (e) {
      // embedded flows cause a cross-origin exception
      return () => {};
    }
  }, [handlerRef, name, options, target]);
}

export function useShowOnLongPress(
  timeout = 2000,
) {
  const ref = React.useMemo(
    () => React.createRef<HTMLElement>(), [],
  );
  const [{ isActive, isHolding }, setState] = React.useState({
    isActive: false,
    isHolding: false,
  });

  // When the user releases the screen, it should remain active for {timeout}
  // milliseconds, unless the user taps away.
  React.useEffect(() => {
    if (isActive && !isHolding) {
      let tmpTimeout: NodeJS.Timeout | null = setTimeout(() => {
        tmpTimeout = null;
        setState({
          isActive: false,
          isHolding: false,
        });
      }, timeout);
      return () => {
        if (tmpTimeout) {
          clearTimeout(tmpTimeout);
        }
      };
    }
    return () => { };
  }, [isActive, isHolding, timeout]);

  // I pulled this out into its own variable to force the `useEffect` to trigger
  // when the target gets set.
  const target = ref.current;

  // Tapping away should instantly clear isActive
  useEventListener(target, 'blur', () => {
    if (isActive) {
      setState({
        isActive: false,
        isHolding: false,
      });
    }
  });
  useEventListener(target, 'touchend', () => {
    if (isActive) {
      setState({
        isActive,
        isHolding: false,
      });
    }
  });

  // A long press on mobile triggers the context menu
  useEventListener(target, 'contextmenu', (event) => {
    if (isActive) {
      event.preventDefault();
    }
  });


  React.useEffect(() => {
    if (!target) {
      return;
    }

    const hammer = new Hammer(target, {
      recognizers: [
        [Hammer.Press],
      ],
    });
    hammer.on('press', () => {
      setState({
        isActive: true,
        isHolding: true,
      });
    });

    return () => {
      hammer.destroy();
    };
  }, [target, ref]);

  const r: [typeof ref, typeof isActive] = [ref, isActive];
  return r;
}

export function useMatchMedia(query: string) {
  const [matches, setMatches] = React.useState(false);
  React.useLayoutEffect(() => {
    // https://caniuse.com/matchmedia
    const mq = window.matchMedia(query);

    const onChange = () => {
      setMatches(mq.matches);
    };

    if (
      process.env.NODE_ENV === 'test'
        && !mq.addEventListener
        && !mq.removeEventListener
    ) {
      // JSDom doesn't implement these
      mq.addEventListener = () => {};
      mq.removeEventListener = () => {};
    }

    // Safari <=13 doesn't have addEventListener.
    if (mq.addEventListener == null) {
      mq.addListener(onChange);
      onChange();
      return () => {
        mq.removeListener(onChange);
      };
    }

    mq.addEventListener('change', onChange);
    onChange();
    return () => {
      mq.removeEventListener('change', onChange);
    };

  }, [query]);

  return matches;
}

export function usePreventNativeZoom() {
  React.useLayoutEffect(() => {
    const viewport = document.querySelector('meta[name=viewport]');
    if (viewport) {
      const content = viewport.getAttribute('content') || '';
      const newContent = content.split(/,\s?/);

      // Prevent browsers from automatically zooming into text fields
      if (!content.includes('maximum-scale=1')) {
        newContent.push('maximum-scale=1');
      }

      if (newContent.join(',') !== viewport.getAttribute('content')) {
        viewport.setAttribute('content', newContent.join(','));
      }

      return () => {
        viewport.setAttribute('content', content);
      };
    }
    return () => { };
  }, []);
}

export function usePreviousValue<T>(value: T): undefined | T {
  const ref = React.useRef(value);
  React.useEffect(() => {
    ref.current = value;
  });

  return ref.current;
}

declare function beforeEach(f: Function): void

let htmlCounter = 1;
if (process.env.NODE_ENV === 'test' && typeof beforeEach === 'function') {
  // Each test needs to reset this so that snapshots don't change.
  beforeEach(() => {
    htmlCounter = 1;
  });
}
export function useHtmlIDs() {
  const id = React.useMemo(() => htmlCounter++, []);
  return (name: string) => `${name}_${id}`;
}


export function useRefReady(ref: React.RefObject<any>) {
  const [ready, setReady] = React.useState(
    ref.current != null
    // jsdom doesn't actually calculate positions, so there is no need to wait
    // for a 2nd render.
    || navigator.userAgent.includes('jsdom'),
  );
  React.useEffect(() => {
    if (!ready && ref.current != null) {
      setReady(true);
    }
  }, [ready, ref]);

  return ready;
}

export function useForceUpdate() {
  // The count is not used or returned. It exists only to cause the component
  // using this hook to re-render.
  const [, setCount] = React.useState(0);

  return React.useCallback(() => {
    // I just picked % 10 because I didn't want it to be able to count
    // infinitely. I think any number would work fine.
    setCount((count) => (count + 1) % 10);
  }, []);
}


type URIComponent = string | number | boolean
type QueryParamsInput = {
  // use undefined to remove an item
  [key: string]: URIComponent | URIComponent[] | undefined
}
export function useQueryParams() {
  const history = useHistory();
  const location = useLocation();
  const searchString = location.search;

  const mergeSearchParams = React.useCallback((value: QueryParamsInput, replace: boolean) => {
    // This uses history.location instead of location so that the dependencies
    // never change. history is mutable, so useCallback will generate one
    // function and keep that function forever.
    const searchParams = queryString.parse(history.location.search.substr(1));
    const newLocation = {
      ...history.location,
      search: queryParams({
        ...searchParams,
        ...value,
      }),
    };

    if (replace) {
      history.replace(newLocation);
    } else {
      history.push(newLocation);
    }
  }, [history]);

  const searchParams = React.useMemo(
    () => queryString.parse(searchString.substr(1)),
    [searchString],
  );

  const tuple: [typeof searchParams, typeof mergeSearchParams] = [searchParams, mergeSearchParams];
  return tuple;
}

export function useOnClickAway(
  isEnabled: boolean,
  callback: (event: Event) => void,
): React.MutableRefObject<HTMLDivElement | null> {
  const ref = React.useRef<HTMLDivElement>(null);

  const handleClick = (event: Event) => {
    if (ref.current && !ref.current.contains(event.target as Node)) {
      callback(event);
    }
  };

  const targetElement = isEnabled ? document.body : null;
  useEventListener(targetElement, 'click', handleClick);
  return ref;
}

export function useShallowCache<T extends object>(value: T): T {
  const refCache = React.useRef<T>(value);
  if (shallowEqual(value, refCache.current)) {
    return refCache.current;
  }
  refCache.current = value;
  return value;
}

let selectNoneCounter = 0;
export function useGlobalDisableSelect(enabled = true) {

  React.useEffect(() => {
    if (!enabled) {
      return () => {};
    }
    selectNoneCounter++;
    document.body.classList.add(styles.disableSelect);

    return () => {
      selectNoneCounter--;
      if (selectNoneCounter === 0) {
        document.body.classList.remove(styles.disableSelect);
      }
    };
  }, [enabled]);
}

// https://usehooks.com/useDebounce/
export function useDebounce<T>(value: T, delay: number) {
  // State and setters for debounced value
  const [debouncedValue, setDebouncedValue] = React.useState(value);
  React.useEffect(
    () => {
      // Update debounced value after delay
      const handler = setTimeout(() => {
        setDebouncedValue(value);
      }, delay);
      // Cancel the timeout if value changes (also on delay change or unmount)
      // This is how we prevent debounced value from updating if value is changed ...
      // .. within the delay period. Timeout gets cleared and restarted.
      return () => {
        clearTimeout(handler);
      };
    },
    [value, delay], // Only re-call effect if value or delay changes
  );
  return debouncedValue;
}

let midnight = moment({ hour: 0, minute: 0 });
let january1 = moment({ month: 0 });
export function useVariableDateFormat(dateStr: string) {
  const date = moment(dateStr);
  const dateFormat = React.useMemo(() => {
    if (date.isAfter(midnight)) {
      return 'h:mm a';
    } else if (date.isAfter(january1)) {
      return 'MMM D';
    } else {
      return 'M/D/YY';
    }
  }, [date]);

  return date.format(dateFormat);
}

if (process.env.NODE_ENV === 'test' && typeof beforeEach === 'function') {
  const m = midnight;
  const j = january1;
  beforeEach(() => {
    midnight = m;
    january1 = j;
  });
}
useVariableDateFormat.setToday = (date: Moment) => {
  if (process.env.NODE_ENV === 'test' && typeof beforeEach === 'function') {
    midnight = moment(date).set('hour', 0).set('minute', 0);
    january1 = moment(date).set('month', 0);
  }
};

export function useOnMount(effect: Parameters<typeof React.useEffect>[0]) {
  React.useEffect(() => {
    return effect();
  }, [/* eslint-disable-line react-hooks/exhaustive-deps */]);
}

/**
 * Clipboard helper.
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/API/Navigator/clipboard
 * @author Nathan Buchar <nbuchar@dropbox.com>
 * @example
 *
 * ```
 * const [isClipboardSupported, copyToClipboard] = useClipboard();
 *
 * // ...
 *
 * {isClipboardSupported && (
 *   <button onClick={() => copyToClipboard('hello')}>
 *     Click me!
 *   </button>}
 * )}
 * ```
 */
export function useClipboard(): [boolean, (value: string) => void] {
  const isClipboardSupported = React.useMemo(() => {
    try {
      return !!navigator.clipboard.writeText;
    } catch (err) {
      return false;
    }
  }, []);

  const copyToClipboard = React.useCallback((value: string): Promise<void> => {
    if (isClipboardSupported) {
      return navigator.clipboard.writeText(value);
    } else {
      return Promise.reject();
    }
  }, [isClipboardSupported]);

  return [isClipboardSupported, copyToClipboard];
}
